import { type RegistrationStatus, type RegistrationType } from '@exam-center/graphql';
import { Button, DateRangeInput, Divider, Flex, Form } from '@shared/components';
import { useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import {
  ProvinceSearchSelection,
  RegistrationStatusSearchSelection,
  RegistrationTypeSearchSelection,
} from '../../components';
import { useExamEventScheduleSearchParams } from '../../hooks';

import { type ProctoringExamEventScheduleFormType } from './types';

export default function ProctoringExamEventScheduleForm() {
  const [
    { startedAt, endedAt },
    { changeRegistrationStatus, changeRegistrationType, changeProvince, changePeriod },
  ] = useExamEventScheduleSearchParams();

  const defaultDateRange: [Date, Date] = useMemo(() => [startedAt, endedAt], [startedAt, endedAt]);

  const startDate = useMemo(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1), []);
  const endDate = useMemo(() => {
    const date = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0);
    date.setHours(23, 59, 59, 999);
    return date;
  }, []);

  const currentMonthDateRange: [Date, Date] = useMemo(
    () => [startDate, endDate],
    [startDate, endDate],
  );

  const formContext = useForm<ProctoringExamEventScheduleFormType>({
    defaultValues: {
      registrationStatus: [],
      registrationType: [],
      province: [],
      dateRange: defaultDateRange,
    },
  });

  const handleResetFilter = () => {
    formContext.reset();
  };

  const handleSubmitFilter = () => {
    const registrationStatus = (
      formContext.getValues('registrationStatus') ?? []
    ).map<RegistrationStatus>((status) => status.value);
    changeRegistrationStatus(registrationStatus);

    const registrationType = (
      formContext.getValues('registrationType') ?? []
    ).map<RegistrationType>((type) => type.value);
    changeRegistrationType(registrationType);

    const province = (formContext.getValues('province') ?? []).map<string>((prov) => prov.value);
    changeProvince(province);

    const dateRange = formContext.getValues('dateRange');
    if (dateRange && dateRange[0] && dateRange[1]) {
      const formEndDate = new Date(dateRange[1]);
      formEndDate.setHours(23, 59, 59, 999);
      changePeriod(dateRange[0], formEndDate);
    } else {
      changePeriod(startedAt, endedAt);
    }
  };

  return (
    <FormProvider {...formContext}>
      <Form>
        <Flex
          sx={(theme) => ({
            flexDirection: 'column',
            gap: theme.spacing(4),
            p: theme.spacing(4),
            width: '100%',
            border: `1px solid ${theme.palette.grey[300]}`,
            borderRadius: theme.shape.rounded(2),
          })}
        >
          <Flex
            sx={(theme) => ({
              gap: theme.spacing(4),
              width: '100%',
            })}
          >
            <RegistrationStatusSearchSelection
              value={formContext.watch('registrationStatus') ?? []}
            />
            <RegistrationTypeSearchSelection value={formContext.watch('registrationType') ?? []} />
            <ProvinceSearchSelection value={formContext.watch('province') ?? []} />
            <DateRangeInput
              name='dateRange'
              label='วันที่'
              value={formContext.watch('dateRange') ?? defaultDateRange}
              defaultValue={[startedAt, endedAt]}
              onClear={() => {
                formContext.setValue('dateRange', currentMonthDateRange);
              }}
              onChange={(value) => {
                if (value && value[0] && value[1]) {
                  formContext.setValue('dateRange', [value[0], value[1]]);
                } else {
                  formContext.setValue('dateRange', defaultDateRange);
                }
              }}
            />
          </Flex>
          <Divider />
          <Flex
            sx={(theme) => ({
              gap: theme.spacing(2),
              justifyContent: 'flex-end',
            })}
          >
            <Button
              variant='outlined'
              color='secondary'
              sx={{ width: '100px' }}
              onClick={handleResetFilter}
            >
              Reset
            </Button>
            <Button variant='contained' sx={{ width: '100px' }} onClick={handleSubmitFilter}>
              Apply
            </Button>
          </Flex>
        </Flex>
      </Form>
    </FormProvider>
  );
}
